import { useCallback, useEffect, useRef, useState } from 'react';
import type { BeadDesign, BeadPalette, CellColorId, EditorTool } from '../types';
import { getPaletteColor } from '../data/palette';
import { floodFill } from '../lib/pattern';

type PatternCanvasProps = {
  design: BeadDesign;
  palette: BeadPalette;
  selectedColorId: CellColorId;
  selectedTool: EditorTool;
  zoom: number;
  showGrid: boolean;
  showSymbols: boolean;
  onPickColor: (colorId: CellColorId) => void;
  onCommitCells: (cells: CellColorId[], label: string) => void;
  labels: {
    editor: string;
    fillRegion: string;
    eraseCells: string;
    paintCells: string;
  };
};

export default function PatternCanvas({
  design,
  palette,
  selectedColorId,
  selectedTool,
  zoom,
  showGrid,
  showSymbols,
  onPickColor,
  onCommitCells,
  labels
}: PatternCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isPaintingRef = useRef(false);
  const changedRef = useRef(false);
  const liveCellsRef = useRef<CellColorId[]>(design.cells);
  const [liveCells, setLiveCells] = useState(design.cells);

  useEffect(() => {
    liveCellsRef.current = design.cells;
    setLiveCells(design.cells);
  }, [design.cells]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const cellSize = zoom;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = design.width * cellSize * dpr;
    canvas.height = design.height * cellSize * dpr;
    canvas.style.width = `${design.width * cellSize}px`;
    canvas.style.height = `${design.height * cellSize}px`;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.scale(dpr, dpr);
    context.clearRect(0, 0, canvas.width, canvas.height);

    liveCells.forEach((colorId, index) => {
      const x = index % design.width;
      const y = Math.floor(index / design.width);
      if (colorId === 'transparent') {
        context.fillStyle = (x + y) % 2 === 0 ? '#f4f7f6' : '#e9efed';
      } else {
        context.fillStyle = getPaletteColor(palette, colorId).hex;
      }
      context.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);

      if (showSymbols && colorId !== 'transparent' && cellSize >= 15) {
        const color = getPaletteColor(palette, colorId);
        context.font = `${Math.max(8, Math.floor(cellSize * 0.42))}px ui-monospace, SFMono-Regular, Menlo, monospace`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = ['white', 'cream', 'yellow', 'tan', 'skin', 'light_gray'].includes(color.id)
          ? '#172126'
          : '#ffffff';
        context.fillText(color.symbol, x * cellSize + cellSize / 2, y * cellSize + cellSize / 2);
      }
    });

    if (showGrid) {
      context.strokeStyle = zoom >= 12 ? 'rgba(23, 33, 38, 0.18)' : 'rgba(23, 33, 38, 0.1)';
      context.lineWidth = 1;
      for (let x = 0; x <= design.width; x += 1) {
        context.beginPath();
        context.moveTo(x * cellSize + 0.5, 0);
        context.lineTo(x * cellSize + 0.5, design.height * cellSize);
        context.stroke();
      }
      for (let y = 0; y <= design.height; y += 1) {
        context.beginPath();
        context.moveTo(0, y * cellSize + 0.5);
        context.lineTo(design.width * cellSize, y * cellSize + 0.5);
        context.stroke();
      }
    }
  }, [design.height, design.width, liveCells, palette, showGrid, showSymbols, zoom]);

  const eventToIndex = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return -1;
      const rect = canvas.getBoundingClientRect();
      const x = Math.floor(((event.clientX - rect.left) / rect.width) * design.width);
      const y = Math.floor(((event.clientY - rect.top) / rect.height) * design.height);
      if (x < 0 || x >= design.width || y < 0 || y >= design.height) return -1;
      return y * design.width + x;
    },
    [design.height, design.width]
  );

  const applyAt = useCallback(
    (index: number) => {
      if (index < 0) return;

      if (selectedTool === 'eyedropper') {
        const colorId = liveCellsRef.current[index];
        if (colorId !== 'transparent') onPickColor(colorId);
        return;
      }

      if (selectedTool === 'fill') {
        const filled = floodFill(
          liveCellsRef.current,
          design.width,
          design.height,
          index,
          selectedColorId
        );
        liveCellsRef.current = filled;
        setLiveCells(filled);
        onCommitCells(filled, labels.fillRegion);
        return;
      }

      const nextColor = selectedTool === 'eraser' ? 'transparent' : selectedColorId;
      if (liveCellsRef.current[index] === nextColor) return;
      const next = [...liveCellsRef.current];
      next[index] = nextColor;
      liveCellsRef.current = next;
      changedRef.current = true;
      setLiveCells(next);
    },
    [design.height, design.width, labels.fillRegion, onCommitCells, onPickColor, selectedColorId, selectedTool]
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const index = eventToIndex(event);
    if (selectedTool === 'pencil' || selectedTool === 'eraser') {
      isPaintingRef.current = true;
      changedRef.current = false;
    }
    applyAt(index);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isPaintingRef.current) return;
    applyAt(eventToIndex(event));
  };

  const finishStroke = () => {
    if (isPaintingRef.current && changedRef.current) {
      onCommitCells(liveCellsRef.current, selectedTool === 'eraser' ? labels.eraseCells : labels.paintCells);
    }
    isPaintingRef.current = false;
    changedRef.current = false;
  };

  return (
    <canvas
      ref={canvasRef}
      className={`pattern-canvas tool-${selectedTool}`}
      aria-label={labels.editor}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishStroke}
      onPointerCancel={finishStroke}
    />
  );
}
